/**
 * 贡献视图契约 —— 归因与贡献（**L 域 L3** · `DR-018` §3.2 · `DR-037`）
 *
 * 依据：L 域 PRD **§5.3**（归因与贡献视图 · 口径 1–8）· **§7.3**（`AC-L3.1`–`L3.14`）·
 * 规格 **§12**（成员贡献分布，已由 `DR-037` 由三桶订正为**两桶**）· `DR-037`（D1–D5）·
 * 架构 **§5.3**（唯一「支出」口径的所有权矩阵）· **§4.4**（`transactions (ledgerId, userId)` 索引用途）。
 *
 * ## 🔴 本文件是**传输层投影**，不是口径层
 *
 * 「记录支出」的聚合**必须**由 C 域唯一支出内核（`nucleus/expense.ts` 的 `composeExpense`）算出 ——
 * `AC-L3.6` 明测「**无第二份聚合实现**」。本文件的类型只描述"服务端算完之后长什么样"，
 * 端侧**不得**据它自行聚合（那正是 `AC-L3.6` 要拦的东西）。
 *
 * ## 🔴 两桶（`DR-037` D1/D2），不是三桶
 *
 * 每一笔支出**必然**落在「我」或「Ta」名下 —— 因为 `transactions.userId`（记录人）是 `NOT NULL`。
 * 于是：**两桶之和 ≡ 总支出**，没有第三态。
 *
 * | 桶 | 判定 |
 * |---|---|
 * | `self` | `transactions.userId` = **调用者本人** |
 * | `other` | `transactions.userId` = 任一其他成员 |
 *
 * 🔴 **本批不产出「共同（Ours）」** —— v1 的 `Ours` 定义（"`userId` 为空的系统生成流水"）
 * 在冻结后的 schema 下**恒为空**，且会让"三桶之和 < 总支出"这个用户可见缺陷成立（`DR-037` §1）。
 *
 * ## 🔴 记录人 ≠ 归属人（`DR-018` §3.2）
 *
 * - **记录人**（`transactions.userId`）= **谁记的这笔**（操作者）—— 本文件的分组维度；
 * - **归属人**（`accounts.ownerId`）= **钱是谁的** —— 见 `AccountView.ownerId` / `ownerName`。
 *
 * 两者分开展示、各有载体：记录人走**流水行徽标**，归属人走**账户卡副标题**（`AC-L3.3` / `AC-L3.4`）。
 * 把记录人当归属人用是本域最需要避免的语义错误（`AC-L3.14` 用"owner 在 member 账户上记一笔"构造验证）。
 */

import { z } from 'zod';
import { LedgerRole } from '../enums/identity.js';

// ═══════════════════════ 窗口取值集（契约字段的取值集，不新开 enums/） ═══════════════════════
//
// ⚠️ 与 `JoinStrategy` / `TxnDirectionFilter` 同例：这是**筛选维度的取值集**，
// 不是"两端各可能写一份"的枚举 ⇒ 就地定义在契约层。新增共享枚举须同时改架构 §4.2
// 与 `P域PRD` §3（`AC-P1.15`）。

/**
 * 贡献视图的统计窗口（L 域 PRD §5.3-6：**默认本月**，可切**近 3 月 / 今年**）。
 *
 * | 值 | 起点 |
 * |---|---|
 * | `thisMonth` | 本月 1 日（**默认**） |
 * | `last3Months` | 本月 1 日往前推 2 个月（含本月共 **3 个自然月**） |
 * | `thisYear` | 本年 1 月 1 日 |
 *
 * 🔴 **一律为账本本地日历日闭区间**（`from` ~ `今天`），由服务端经 `nucleus/calendar.ts`
 * 算出，**端侧只传枚举值、不自己算日期**（否则 Web 与小程序会在月末差一天）。
 */
export const ContributionWindow = {
  ThisMonth: 'thisMonth',
  Last3Months: 'last3Months',
  ThisYear: 'thisYear',
} as const;
export type ContributionWindow = (typeof ContributionWindow)[keyof typeof ContributionWindow];

/** 窗口枚举的可选列表（用于 `z.enum` 与端侧下拉 —— 单一源） */
export const CONTRIBUTION_WINDOWS = [
  ContributionWindow.ThisMonth,
  ContributionWindow.Last3Months,
  ContributionWindow.ThisYear,
] as const;

/**
 * `GET /api/ledgers/contribution` 的查询参数。
 *
 * ⚠️ `window` **不在契约层做默认值填充** —— 缺省时用 {@link ContributionWindow.ThisMonth}
 * 是**服务端口径**（PRD §5.3-6「默认本月」），把它写进 `.default()` 会让"契约层偷偷定产品口径"
 * 与"服务端定口径"两处并存。⇒ 本 schema 只做**形态校验**，缺省由服务端补。
 */
export const ContributionQuerySchema = z.object({
  window: z.enum(CONTRIBUTION_WINDOWS).optional(),
});
export type ContributionQuery = z.infer<typeof ContributionQuerySchema>;

// ═══════════════════════ 视图类型 ═══════════════════════

/**
 * 一个贡献桶（= 一位成员在这个窗口里的贡献）。
 *
 * 🔴 `expense` **必须**来自 C 域唯一支出内核（`composeExpense`）的 `total`，
 * 🔴 `accountCount` **不含** `internal` 挂账账户（`AC-L3.8`）。
 */
export interface ContributionBucketView {
  /** 该桶对应的成员（记录人） */
  userId: string;
  /** 展示名（`users.name`） */
  userName: string;
  /** 该成员在本账本的角色（前端据此显示"所有者 / 成员"） */
  role: LedgerRole;
  /** 是否为调用者本人 —— 端侧据此判"我"与"Ta"（**避免端侧自己比对 id**，同 `LedgerMemberView.isSelf`） */
  isSelf: boolean;
  /**
   * 记录支出（**该成员为记录人的支出合计**）—— 原币整数分，**恒 ≥ 0**。
   *
   * 🔴 走唯一支出口径（三段式：expense 腿 − 冲减腿 + 待摊摊销）。
   * ⚠️ **多币种不折算相加**（`DR-004`）—— 本字段**只有在本账本单一币种时才有意义**；
   * 账本含多币种账户时服务端置 `currencyMixed = true` 并**不给出跨币种合计数**（见下）。
   */
  expense: number;
  /**
   * 该成员**名下有账户数**（`accounts.ownerId = userId` 且非隐藏）—— 🔴 **不含 `internal` 挂账账户**（`AC-L3.8`）。
   *
   * ⚠️ 注意维度：`expense` 按**记录人**分组，`accountCount` 按**归属人**统计。
   * 二者语义不同是**刻意的**（`DR-037` D5：「谁花的」与「谁记的」本就分开展示）——
   * 但 UI 上必须**分开标注**，不能并成一行"贡献"（否则就是 `DR-037` §2 否决的"一条判据两种口径"）。
   */
  accountCount: number;
}

/**
 * 贡献视图（`GET /api/ledgers/contribution` 的响应）—— 🔴 **两桶**（`DR-037`）。
 *
 * ## 为什么是 `buckets` 数组而不是 `{ mine, theirs }` 两个字段
 * 成员上限 4（`Collaboration.MemberLimit`）⇒ 除"我"之外**可能有 1–3 位**其他成员
 * （甲 owner + 乙丙丁）。写成固定两字段会**悄悄丢掉**丙丁的贡献 ——
 * 而"钱对不上"正是 `DR-037` §1 要消除的那个缺陷。
 * ⇒ 结构上允许 N 个桶，**排序保证「我」恒在首位**（见 `buckets` 说明），
 * 端侧按 `isSelf` 渲染"我 / Ta"两栏即可。
 */
export interface ContributionView {
  /** 窗口枚举回显（缺省请求时为服务端补的默认值 —— `thisMonth`） */
  window: ContributionWindow;
  /** 窗口起点（`YYYY-MM-DD` · 账本本地日历日）—— 端侧直接展示，**不自己算** */
  from: string;
  /** 窗口终点（`YYYY-MM-DD` · = 账本"今天"） */
  to: string;
  /**
   * 贡献桶列表 —— 🔴 **排序：调用者本人恒在首位**，其余按 `expense` 降序、
   * 同额按 `userName` 升序（稳定序，服务端保证）。
   *
   * ⚠️ **不排名、不评分**（`AC-L3.9` 红线）—— 排序只是为了"我先看到自己"，
   * UI **不得**把序号渲染成名次（无 ①②③、无"第 N 名"、无奖杯）。
   */
  buckets: readonly ContributionBucketView[];
  /**
   * 全部桶之和（= **总支出**，`DR-037` D2 的结构性保证）。
   *
   * 🔴 **它必须等于** `Σ buckets[].expense` —— 这不是"另算一个数"，而是把
   * "两桶之和 = 总支出"这条不变量**显式暴露出来**（集成测试直接断言它成立）。
   * 若哪天它 ≠ Σ，说明有支出既不属于"我"也不属于任何成员 ⇒ 记录人维度漏了人。
   */
  totalExpense: number;
  /**
   * 🔴 账本是否**含多币种**（账户币种不唯一）。
   *
   * 为 `true` 时：`expense` / `totalExpense` **只统计账本的基础币种账户**，
   * 且 UI 必须显示"仅统计 ¥ 账户"之类的限定说明 —— 因为 `DR-004` 明禁跨币种相加，
   * 这里**绝不**把 USD 账户的支出折算成 CNY 混进同一个数。
   *
   * ⚠️ 本字段的诚实性同 `NetWorthResponse.partial`：宁可说"只统计了一部分"，
   * 也不给一个看起来完整、实则口径错误的合计数。
   */
  currencyMixed: boolean;
  /**
   * 🔴 单人账本 ⇒ 本字段为 `true`，且 `buckets` **只有一个元素**（调用者自己）。
   *
   * 端侧据此**整块隐藏**贡献视图、改显示「邀请家人一起记」引导卡（`AC-L3.10` / `AC-L7.2`）。
   * ⚠️ 判定放在服务端（`ledger_members` 成员数）而不是让端侧数 `buckets.length` ——
   * 「单人账本」是**成员关系**的事实，不是"贡献桶恰好一个"的巧合。
   */
  singleMember: boolean;
}

/**
 * 归因事实的轻量投影（`AC-L3.5`：详情抽屉显示**账户所有者 + 记录人 + 创建/修改时间**）。
 *
 * ⚠️ 详情抽屉主数据走 `GET /api/transactions/:id`（`TransactionDetailView`）；
 * 本类型只是把"抽屉里归因那三行"的表达收成一处，**避免端侧各拼一遍字段名**。
 * 🔴 `recorder` 是**记录人**（`transactions.userId`），`accountOwner` 是**归属人**
 * （腿所属账户的 `accounts.ownerId`）—— 两者**独立**，不可互相推导（`AC-L3.14`）。
 */
export interface AttributionView {
  /** 记录人（谁记的）—— 不可改（`AC-L3.12`） */
  recorder: { userId: string; name: string };
  /**
   * 账户所有者（钱是谁的）—— 仅 owner 可改（`AC-L3.11`）。
   *
   * ⚠️ 一笔交易可能有多条腿、分属**不同归属人**的账户 ⇒ 这里给**去重后的列表**。
   * 单腿交易（绝大多数）就一个元素。UI 显示"归属：甲"；多归属时显示"甲、乙"。
   */
  accountOwners: readonly { userId: string; name: string }[];
  /** 创建时刻（UTC 毫秒） */
  createdAt: number;
  /** 最后修改时刻（UTC 毫秒） */
  updatedAt: number;
}
